import { ref, onUnmounted } from '@vue/composition-api';
import { errorMessage } from '@/utils/ErrorMessages';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 3000;

function useDashboard() {
  const loading = ref(false);
  const error = ref(null);
  const readerRef = ref(null);
  const retryTimerRef = ref(null);
  const data = ref({
    totalSummary: null,
    totalAccounts: null,
    activeAccounts: null,
    revenuePerMonth: null,
    loadsByAccount: null,
    topAccountsByLoads: null,
    topAccountsByRevenue: null,
    loadsQuoted: null,
    loadsAssigned: null,
    loadsInTransit: null,
    loadsDelivered: null,
  });

  let activeSessionId = 0;
  let pendingRetryResolve = null;

  function cancelReader() {
    if (readerRef.value) {
      readerRef.value.cancel().catch(() => {});
      readerRef.value = null;
    }
  }

  function clearRetryTimer() {
    if (retryTimerRef.value) {
      clearTimeout(retryTimerRef.value);
      retryTimerRef.value = null;
    }
    if (pendingRetryResolve) {
      const resolveFn = pendingRetryResolve;
      pendingRetryResolve = null;
      resolveFn();
    }
  }

  function waitBeforeRetry() {
    return new Promise((resolve) => {
      pendingRetryResolve = resolve;
      retryTimerRef.value = setTimeout(() => {
        retryTimerRef.value = null;
        pendingRetryResolve = null;
        resolve();
      }, RETRY_DELAY_MS);
    });
  }

  async function openStream({ token, queryString }, onConnected) {
    cancelReader();

    const url = `${process.env.VUE_APP_BACK_ROUTE}/api/statistics/dashboard?${queryString}`;

    const res = await fetch(url, {
      headers: {
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) { throw new Error(`HTTP ${res.status}`); }

    const reader = res.body.getReader();
    readerRef.value = reader;
    const decoder = new TextDecoder();
    let buffer = '';
    let connected = false;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) { break; }

      if (!connected) {
        connected = true;
        onConnected();
      }

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();

      // eslint-disable-next-line no-restricted-syntax
      for (const part of parts) {
        const line = part.trim();
        // eslint-disable-next-line no-continue
        if (!line.startsWith('data:')) { continue; }
        try {
          const { name, data: payload } = JSON.parse(line.slice(5).trim());
          if (name in data.value) {
            data.value[name] = payload;
          }
        } catch (parseError) {
          // Ignore malformed SSE chunks; the stream continues with the next event.
        }
      }
    }
  }

  async function fetchDashboard({
    token,
    startDate,
    endDate,
    childAccountIds,
  }) {
    clearRetryTimer();
    cancelReader();

    activeSessionId += 1;
    const sessionId = activeSessionId;
    loading.value = true;
    error.value = null;

    Object.keys(data.value).forEach((dataKey) => { data.value[dataKey] = null; });

    const params = new URLSearchParams();
    if (startDate) { params.append('start_date', startDate); }
    if (endDate) { params.append('end_date', endDate); }
    childAccountIds.forEach((accountId) => { params.append('child_account_ids', accountId); });
    const queryString = params.toString();

    let retryCount = 0;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (sessionId !== activeSessionId) { return; }

        try {
          // eslint-disable-next-line no-await-in-loop, no-loop-func
          await openStream({ token, queryString }, () => {
            retryCount = 0;
            error.value = null;
          });
          break;
        } catch (networkError) {
          if (sessionId !== activeSessionId) { return; }

          if (retryCount >= MAX_RETRIES) {
            error.value = networkError.message;
            errorMessage('Unable to load dashboard data');
            break;
          }

          retryCount += 1;
          error.value = 'Reconnecting...';
          clearRetryTimer();
          // eslint-disable-next-line no-await-in-loop
          await waitBeforeRetry();
        }
      }
    } finally {
      if (sessionId === activeSessionId) {
        loading.value = false;
      }
    }
  }

  onUnmounted(() => {
    activeSessionId += 1;
    clearRetryTimer();
    cancelReader();
  });

  return {
    data,
    loading,
    error,
    fetchDashboard,
  };
}

export default useDashboard;
